import {useEffect, useState} from 'react';
import {Link, useSearchParams} from 'react-router';
import {useCargarSeguimientoDore, useExportacionesDore} from '../api/dore';
import {DEFINICION_DORE} from '../componentes/columnas-dore';
import {CampoDeArchivo} from '../componentes/elegir-archivo';
import {Paginacion} from '../componentes/paginacion';
import {SelectorDeColumnas} from '../componentes/selector-de-columnas';
import {SelectorMultiple} from '../componentes/selector-multiple';
import {usePermisos} from '../componentes/sesion-contexto';
import {Tabla} from '../componentes/tabla';
import {useColumnas} from '../componentes/usar-columnas';
import {tr} from '../i18n/idioma';
import {fechaHora, numero, usd} from '../utilidades/formato';

const CLAVES = [
  'etapa',
  'faltan',
  'intermediaria',
  'comprador',
  'proveedor',
  'anio',
  'desde',
  'hasta',
  'buscar',
  'pagina',
  'porPagina',
  'orden',
  'direccion',
] as const;
const campo = 'mt-0.5 w-full rounded-md border border-linea bg-white px-2 py-1 text-sm text-tinta';
const BOTON = 'rounded-md border border-linea bg-white px-2 py-0.5 text-xs hover:bg-papel';

// Dore (decision 0057): el seguimiento de las exportaciones de dore desde
// 2018. La creacion de exportaciones (con sus documentos) y la revision de
// cobros y pagos llegan en las siguientes entregas.
export function PaginaDore() {
  const [parametros, setParametros] = useSearchParams();
  const permisos = usePermisos();
  const consulta = new URLSearchParams();
  for (const clave of CLAVES) {
    const v = parametros.get(clave);
    if (v) consulta.set(clave, v);
  }
  const datos = useExportacionesDore(consulta.toString());
  const cambiar = (cambios: Partial<Record<(typeof CLAVES)[number], string | undefined>>) => {
    const nuevos = new URLSearchParams(parametros);
    for (const [k, v] of Object.entries(cambios)) {
      if (v) nuevos.set(k, v);
      else nuevos.delete(k);
    }
    if (!('pagina' in cambios)) nuevos.delete('pagina');
    setParametros(nuevos, {replace: true});
  };
  const [busqueda, setBusqueda] = useState(parametros.get('buscar') ?? '');
  useEffect(() => {
    const temporizador = setTimeout(() => {
      if ((parametros.get('buscar') ?? '') !== busqueda.trim()) cambiar({buscar: busqueda.trim() || undefined});
    }, 350);
    return () => clearTimeout(temporizador);
  });
  const [visibles, cambiarColumnas] = useColumnas(DEFINICION_DORE);
  const d = datos.data;
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-verde-oscuro">Doré</h1>
          <p className="max-w-3xl text-sm text-apagado">
            {tr(
              'Seguimiento de las exportaciones de doré: quién vende, quién compra, el material, las fechas, los cobros, los pagos y el ensayo. Las fechas con ≈ las calculaba el archivo (no son reales).',
              'Doré export tracking: who sells, who buys, the material, dates, collections, payments and the assay. Dates marked ≈ were calculated by the file (not actual).',
            )}
            {d?.carga &&
              tr(
                ` Datos de ${d.carga.archivo}, cargado el ${fechaHora(d.carga.fecha)}.`,
                ` Data from ${d.carga.archivo}, loaded on ${fechaHora(d.carga.fecha)}.`,
              )}
          </p>
        </div>
        <div className="flex flex-wrap items-start gap-2">
          {permisos.editar && (
            <Link
              to="/dore/nueva"
              className="rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro"
            >
              {tr('＋ Nueva exportación', '＋ New export')}
            </Link>
          )}
          {permisos.administrar && <CargaDeSeguimientoDore />}
        </div>
      </div>
      <div className="grid items-end gap-2 rounded-lg border border-linea bg-white px-3 py-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
        <label className="block text-xs text-apagado sm:col-span-2">
          {tr('Buscar', 'Search')}
          <input
            type="search"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder={tr(
              'Exportación, empresa, factura, guía o comentario',
              'Export, company, invoice, waybill or comment',
            )}
            className="mt-0.5 w-full rounded-md border border-linea px-3 py-1 text-sm text-tinta"
          />
        </label>
        <SelectorMultiple
          etiqueta={tr('Año', 'Year')}
          valor={parametros.get('anio') ?? undefined}
          opciones={d?.opciones.anios ?? []}
          onCambiar={v => cambiar({anio: v})}
        />
        <SelectorMultiple
          etiqueta={tr('Etapa', 'Stage')}
          valor={parametros.get('etapa') ?? undefined}
          opciones={d?.opciones.etapas ?? []}
          onCambiar={v => cambiar({etapa: v})}
        />
        <SelectorMultiple
          etiqueta={tr('Falta', 'Missing')}
          valor={parametros.get('faltan') ?? undefined}
          opciones={d?.opciones.faltan ?? []}
          onCambiar={v => cambiar({faltan: v})}
        />
        <SelectorMultiple
          etiqueta={tr('Intermediaria', 'Intermediary')}
          valor={parametros.get('intermediaria') ?? undefined}
          opciones={d?.opciones.intermediarias ?? []}
          onCambiar={v => cambiar({intermediaria: v})}
        />
        <SelectorMultiple
          etiqueta={tr('Comprador final', 'Final buyer')}
          valor={parametros.get('comprador') ?? undefined}
          opciones={d?.opciones.compradores ?? []}
          onCambiar={v => cambiar({comprador: v})}
        />
        <SelectorMultiple
          etiqueta={tr('Proveedor', 'Supplier')}
          valor={parametros.get('proveedor') ?? undefined}
          opciones={d?.opciones.proveedores ?? []}
          onCambiar={v => cambiar({proveedor: v})}
        />
        <label className="block text-xs text-apagado">
          {tr('Documentos desde', 'Documents from')}
          <input
            type="date"
            value={parametros.get('desde') ?? ''}
            onChange={e => cambiar({desde: e.target.value || undefined})}
            className={campo}
          />
        </label>
        <label className="block text-xs text-apagado">
          {tr('Documentos hasta', 'Documents to')}
          <input
            type="date"
            value={parametros.get('hasta') ?? ''}
            onChange={e => cambiar({hasta: e.target.value || undefined})}
            className={campo}
          />
        </label>
      </div>
      {datos.isError && <p className="text-sm text-alerta">{datos.error.message}</p>}
      {d && (
        <p className="text-xs text-apagado tabular-nums" data-prueba="totales-dore">
          {tr(
            `${numero(d.total)} exportaciones · ${numero(d.totales.barras)} barras · oro ${numero(d.totales.oroG, 2)} g · plata ${numero(d.totales.plataG, 2)} g · venta ${usd(d.totales.valorVentaUsd)} · ${numero(d.totales.conFaltantes)} con datos faltantes`,
            `${numero(d.total)} exports · ${numero(d.totales.barras)} bars · gold ${numero(d.totales.oroG, 2)} g · silver ${numero(d.totales.plataG, 2)} g · sales ${usd(d.totales.valorVentaUsd)} · ${numero(d.totales.conFaltantes)} with missing data`,
          )}
        </p>
      )}
      <div className="space-y-1">
        <div className="flex justify-end">
          <SelectorDeColumnas
            definicion={DEFINICION_DORE}
            visibles={visibles}
            onCambiar={cambiarColumnas}
            claseDelBoton={BOTON}
          />
        </div>
        <div className={datos.isFetching ? 'opacity-70' : ''}>
          <Tabla
            filas={d?.elementos ?? []}
            definicion={DEFINICION_DORE}
            columnasVisibles={visibles}
            claveDeFila={x => x.id}
            orden={parametros.get('orden') ?? ''}
            direccion={parametros.get('direccion') === 'desc' ? 'desc' : 'asc'}
            onOrdenar={o =>
              cambiar({
                orden: o,
                direccion: parametros.get('orden') === o && parametros.get('direccion') !== 'desc' ? 'desc' : 'asc',
              })
            }
            onReordenar={cambiarColumnas}
            altoMaximo="calc(100vh - 20rem)"
            numeracionDesde={d ? (d.pagina - 1) * d.porPagina + 1 : 1}
            mensajeVacio={
              datos.isPending
                ? tr('Cargando…', 'Loading…')
                : tr('No hay exportaciones de doré con estos filtros.', 'No doré exports with these filters.')
            }
            celda={(columna, x) => {
              switch (columna.id) {
                case 'codigo':
                  return (
                    <Link to={`/dore/${encodeURIComponent(x.codigo)}`} className="text-verde hover:underline">
                      {x.codigo}
                    </Link>
                  );
                case 'faltan':
                  return x.faltan.length ? <span className="text-alerta">{columna.valor(x)}</span> : undefined;
                case 'comentarios':
                  return (
                    <span className="block max-w-52 truncate text-xs" title={x.comentarios ?? undefined}>
                      {x.comentarios ?? ''}
                    </span>
                  );
                default:
                  return undefined;
              }
            }}
          />
        </div>
      </div>
      {d && (
        <Paginacion
          pagina={d.pagina}
          totalPaginas={d.totalPaginas}
          porPagina={d.porPagina}
          total={d.total}
          onPagina={p => cambiar({pagina: String(p)})}
          onPorPagina={n => cambiar({porPagina: String(n)})}
          unidad={{es: 'exportaciones', en: 'exports'}}
        />
      )}
    </section>
  );
}

// Subir el Seguimiento.xlsx de DORE EXPORTS (solo administradores). Primero
// se revisa; lo editado en la app no se toca.
export function CargaDeSeguimientoDore({abiertaAlInicio = false}: {abiertaAlInicio?: boolean}) {
  const [abierta, setAbierta] = useState(abiertaAlInicio);
  const [archivo, setArchivo] = useState<File | null>(null);
  const cargar = useCargarSeguimientoDore();
  const r = cargar.data;
  if (!abierta)
    return (
      <button
        type="button"
        onClick={() => setAbierta(true)}
        className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel"
      >
        {tr('⬆ Cargar seguimiento de doré', '⬆ Load doré tracking')}
      </button>
    );
  return (
    <div className="w-full space-y-3 rounded-lg border border-linea bg-white p-4" data-prueba="carga-dore">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-medium">{tr('Cargar el seguimiento de doré', 'Load the doré tracking')}</h2>
          <p className="text-xs text-apagado">
            {tr(
              'El archivo Seguimiento.xlsx de la carpeta DORE EXPORTS (hoja Data). Primero se revisa y nada se guarda hasta que confirmes. Las exportaciones editadas en la app no se tocan.',
              'The Seguimiento.xlsx file from the DORE EXPORTS folder (Data sheet). It is reviewed first and nothing is saved until you confirm. Exports edited in the app are not touched.',
            )}
          </p>
        </div>
        <button type="button" onClick={() => setAbierta(false)} className="text-apagado hover:text-tinta">
          ×
        </button>
      </div>
      <CampoDeArchivo
        etiqueta={tr('Seguimiento de doré (.xlsx)', 'Doré tracking (.xlsx)')}
        accept=".xlsx"
        archivos={archivo ? [archivo] : []}
        onElegir={([a]) => {
          setArchivo(a ?? null);
          cargar.reset();
        }}
      />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!archivo || cargar.isPending}
          onClick={() => archivo && cargar.mutate({archivo, modo: 'revisar'})}
          className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel disabled:opacity-50"
        >
          {tr('Revisar', 'Review')}
        </button>
        {r && !r.aplicado && (
          <button
            type="button"
            disabled={cargar.isPending}
            onClick={() => archivo && cargar.mutate({archivo, modo: 'aplicar'})}
            className="rounded-md bg-verde-oscuro px-3 py-1.5 text-sm text-white disabled:opacity-50"
          >
            {tr('Confirmar y guardar', 'Confirm and save')}
          </button>
        )}
        {cargar.isPending && <span className="text-sm text-apagado">{tr('Leyendo…', 'Reading…')}</span>}
      </div>
      {cargar.isError && <p className="text-sm text-alerta">{cargar.error.message}</p>}
      {r && (
        <div className="space-y-1 text-sm" role="status">
          <p className="font-medium">
            {r.aplicado
              ? tr('Guardado.', 'Saved.')
              : tr('Revisión (todavía no se guardó nada):', 'Review (nothing saved yet):')}
          </p>
          <p>
            {tr(
              `${numero(r.filas)} filas: ${numero(r.nuevas)} exportaciones nuevas y ${numero(r.actualizadas)} actualizadas; ${numero(r.editadasEnLaApp)} editadas en la app no se tocan.`,
              `${numero(r.filas)} rows: ${numero(r.nuevas)} new exports and ${numero(r.actualizadas)} updated; ${numero(r.editadasEnLaApp)} edited in the app are not touched.`,
            )}
          </p>
          <p>
            {tr(
              `Cobros: ${numero(r.cobros)} · pagos al proveedor: ${numero(r.pagos)} · ensayos: ${numero(r.ensayos)} · liquidaciones: ${numero(r.liquidaciones)} · fechas estimadas: ${numero(r.fechasEstimadas)}.`,
              `Collections: ${numero(r.cobros)} · supplier payments: ${numero(r.pagos)} · assays: ${numero(r.ensayos)} · settlements: ${numero(r.liquidaciones)} · estimated dates: ${numero(r.fechasEstimadas)}.`,
            )}
          </p>
          {r.empresasNuevas.length > 0 && (
            <p className="text-apagado">
              {tr(
                `Empresas nuevas (si alguna ya existe con otro nombre, únela en Campos y catálogos → Empresas): ${r.empresasNuevas.join(', ')}.`,
                `New companies (if one already exists under another name, merge it in Fields and catalogs → Companies): ${r.empresasNuevas.join(', ')}.`,
              )}
            </p>
          )}
          {r.avisos.length > 0 && (
            <ul className="list-disc pl-5 text-xs">
              {r.avisos.slice(0, 30).map(a => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
